import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';

const AudioPlayerBar = ({ 
  audioSource = null,
  initialPlaybackState = 'paused',
  onPlayPause,
  onSeek,
  onVolumeChange,
  onNext,
  onPrevious
}) => {
  const [playbackState, setPlaybackState] = useState(initialPlaybackState);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    // Reset error state when new source is provided
    setHasError(false);

    if (audioSource?.url) {
      // Validate URL format before setting
      try {
        new URL(audioSource.url);
        audio.src = audioSource?.url;
        audio?.load();
      } catch (error) {
        console.warn('Invalid audio URL format:', audioSource?.url);
        setHasError(true);
      }
    } else {
      // Clear source if no valid URL
      audio?.removeAttribute('src');
      audio?.load();
    }
  }, [audioSource]);

  const handlePlayPause = () => {
    if (hasError || !audioSource?.url) {
      console.warn('Cannot play: invalid or missing audio source');
      return;
    }

    const newState = playbackState === 'playing' ? 'paused' : 'playing';
    setPlaybackState(newState);
    
    if (audioRef?.current) {
      const playPromise = newState === 'playing' 
        ? audioRef?.current?.play()
        : Promise.resolve(audioRef?.current?.pause());

      playPromise?.catch((error) => {
        console.error('Playback error:', error);
        setPlaybackState('paused');
        setHasError(true);
      });
    }
    
    onPlayPause?.(newState);
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e?.target?.value);
    setCurrentTime(seekTime);
    
    if (audioRef?.current) {
      audioRef.current.currentTime = seekTime;
    }
    
    if (onSeek) {
      onSeek(seekTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e?.target?.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    
    if (audioRef?.current) {
      audioRef.current.volume = newVolume;
    }
    
    if (onVolumeChange) {
      onVolumeChange(newVolume);
    }
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    if (audioRef?.current) {
      audioRef.current.muted = newMutedState;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio?.currentTime || 0);
    const updateDuration = () => setDuration(audio?.duration || 0);
    const handleEnded = () => setPlaybackState('paused');
    const handleError = (e) => {
      // Detailed error logging for debugging
      const errorDetails = {
        error: e?.target?.error,
        networkState: audio?.networkState,
        readyState: audio?.readyState,
        currentSrc: audio?.currentSrc
      };
      console.warn('Audio playback error:', errorDetails);
      setHasError(true);
      setPlaybackState('paused');
    };
    const handleCanPlay = () => setHasError(false);
    const handleLoadStart = () => setHasError(false);

    audio?.addEventListener('timeupdate', updateTime);
    audio?.addEventListener('loadedmetadata', updateDuration);
    audio?.addEventListener('ended', handleEnded);
    audio?.addEventListener('error', handleError);
    audio?.addEventListener('canplay', handleCanPlay);
    audio?.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio?.removeEventListener('timeupdate', updateTime);
      audio?.removeEventListener('loadedmetadata', updateDuration);
      audio?.removeEventListener('ended', handleEnded);
      audio?.removeEventListener('error', handleError);
      audio?.removeEventListener('canplay', handleCanPlay);
      audio?.removeEventListener('loadstart', handleLoadStart);
    };
  }, []);

  if (!audioSource) {
    return null;
  }

  return (
    <>
      <div className="audio-player-bar hidden lg:block fixed right-0 top-16 bottom-0 w-80 bg-card border-l border-border z-[200] shadow-soft-lg">
        <audio 
          ref={audioRef}
          preload="metadata"
          crossOrigin="anonymous"
        />
        
        <div className="audio-player-content h-full flex flex-col p-6">
          {hasError && (
            <div className="error-message mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-xs text-destructive">
                Ошибка загрузки аудио. Проверьте источник.
              </p>
            </div>
          )}

          <div className="audio-player-artwork mb-6">
            <div className="artwork-container aspect-square rounded-lg overflow-hidden bg-muted shadow-soft">
              <Image 
                src={audioSource?.artwork || '/assets/images/no_image.png'} 
                alt={audioSource?.title || 'Audio artwork'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="audio-player-info mb-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-1 line-clamp-2">
              {audioSource?.title || 'Без названия'}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {audioSource?.artist || 'Неизвестный исполнитель'}
            </p>
          </div>

          <div className="audio-player-progress mb-6">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
              aria-label="Прогресс воспроизведения"
            />
            <div className="flex justify-between mt-2">
              <span className="text-xs font-mono text-muted-foreground">
                {formatTime(currentTime)}
              </span>
              <span className="text-xs font-mono text-muted-foreground">
                {formatTime(duration)}
              </span>
            </div>
          </div>

          <div className="audio-player-controls flex items-center justify-center gap-4 mb-6">
            <button
              onClick={onPrevious}
              className="control-button p-2 rounded-full hover:bg-muted transition-colors duration-200"
              aria-label="Предыдущий трек"
            >
              <Icon name="SkipBack" size={20} color="var(--color-foreground)" />
            </button>
            
            <button
              onClick={handlePlayPause}
              className="control-button-primary p-4 rounded-full bg-primary hover:bg-primary/90 transition-all duration-200 active:scale-95"
              aria-label={playbackState === 'playing' ? 'Пауза' : 'Воспроизвести'}
            >
              <Icon 
                name={playbackState === 'playing' ? 'Pause' : 'Play'} 
                size={24} 
                color="var(--color-primary-foreground)" 
              />
            </button>
            
            <button
              onClick={onNext}
              className="control-button p-2 rounded-full hover:bg-muted transition-colors duration-200"
              aria-label="Следующий трек"
            >
              <Icon name="SkipForward" size={20} color="var(--color-foreground)" />
            </button>
          </div>

          <div className="audio-player-volume flex items-center gap-3">
            <button
              onClick={toggleMute}
              className="volume-button p-2 rounded-full hover:bg-muted transition-colors duration-200"
              aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
            >
              <Icon 
                name={isMuted ? 'VolumeX' : volume > 0.5 ? 'Volume2' : 'Volume1'} 
                size={20} 
                color="var(--color-foreground)" 
              />
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="flex-1 h-1 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
              aria-label="Громкость"
            />
          </div>
        </div>
      </div>
      <div className="mobile-audio-player lg:hidden fixed bottom-16 left-0 right-0 z-[200] bg-card border-t border-border shadow-soft-lg">
        <audio 
          ref={audioRef}
          preload="metadata"
          crossOrigin="anonymous"
        />
        
        {hasError && (
          <div className="error-banner p-2 bg-destructive/10 border-b border-destructive/20">
            <p className="text-xs text-destructive text-center">
              Ошибка загрузки аудио
            </p>
          </div>
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mobile-player-toggle w-full p-4 flex items-center gap-3"
        >
          <div className="player-artwork w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <Image 
              src={audioSource?.artwork || '/assets/images/no_image.png'} 
              alt={audioSource?.title || 'Audio artwork'}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="player-info flex-1 text-left min-w-0">
            <h4 className="text-sm font-heading font-semibold text-foreground truncate">
              {audioSource?.title || 'Без названия'}
            </h4>
            <p className="text-xs text-muted-foreground truncate">
              {audioSource?.artist || 'Неизвестный исполнитель'}
            </p>
          </div>
          
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handlePlayPause();
            }}
            className="player-control p-2 rounded-full bg-primary hover:bg-primary/90 transition-colors duration-200"
            aria-label={playbackState === 'playing' ? 'Пауза' : 'Воспроизвести'}
          >
            <Icon 
              name={playbackState === 'playing' ? 'Pause' : 'Play'} 
              size={20} 
              color="var(--color-primary-foreground)" 
            />
          </button>
        </button>

        {isExpanded && (
          <div className="mobile-player-expanded p-4 pt-0 animate-fade-in">
            <div className="player-progress mb-4">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-1 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
                aria-label="Прогресс воспроизведения"
              />
              <div className="flex justify-between mt-2">
                <span className="text-xs font-mono text-muted-foreground">
                  {formatTime(currentTime)}
                </span>
                <span className="text-xs font-mono text-muted-foreground">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            <div className="player-controls flex items-center justify-center gap-6 mb-4">
              <button
                onClick={onPrevious}
                className="control-button p-2"
                aria-label="Предыдущий трек"
              >
                <Icon name="SkipBack" size={24} color="var(--color-foreground)" />
              </button>
              
              <button
                onClick={handlePlayPause}
                className="control-button-primary p-4 rounded-full bg-primary hover:bg-primary/90 transition-all duration-200 active:scale-95"
                aria-label={playbackState === 'playing' ? 'Пауза' : 'Воспроизвести'}
              >
                <Icon 
                  name={playbackState === 'playing' ? 'Pause' : 'Play'} 
                  size={28} 
                  color="var(--color-primary-foreground)" 
                />
              </button>
              
              <button
                onClick={onNext}
                className="control-button p-2"
                aria-label="Следующий трек"
              >
                <Icon name="SkipForward" size={24} color="var(--color-foreground)" />
              </button>
            </div>

            <div className="player-volume flex items-center gap-3">
              <button
                onClick={toggleMute}
                className="volume-button p-2"
                aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
              >
                <Icon 
                  name={isMuted ? 'VolumeX' : volume > 0.5 ? 'Volume2' : 'Volume1'} 
                  size={20} 
                  color="var(--color-foreground)" 
                />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="flex-1 h-1 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
                aria-label="Громкость"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AudioPlayerBar;